import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { withStyles } from '@material-ui/core/styles';

import PlaygroundTimetableEmbed from './PlaygroundTimetableEmbed';

export function playgroundIdOf(playground) {
  if (!playground) return '';
  const id = playground.id != null ? playground.id : playground.Id;
  return id != null && id !== '' ? String(id) : '';
}

export function playgroundNameOf(playground) {
  if (!playground) return '';
  return playground.name || playground.Name || `Площадка #${playgroundIdOf(playground)}`;
}

export class UnifiedHallSchedule extends Component {
  static propTypes = {
    playgrounds: PropTypes.array,
    selectedPlaygroundId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onPlaygroundChange: PropTypes.func,
    sportComplexId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    history: PropTypes.object,
    initialSectionFilterId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    classes: PropTypes.object.isRequired,
  };

  static defaultProps = {
    playgrounds: [],
    selectedPlaygroundId: '',
    onPlaygroundChange: null,
    sportComplexId: null,
    history: null,
    initialSectionFilterId: '',
  };

  resolvedPlaygroundId = () => {
    const { playgrounds, selectedPlaygroundId } = this.props;
    if (selectedPlaygroundId) return String(selectedPlaygroundId);
    const first = (playgrounds || [])[0];
    return playgroundIdOf(first);
  };

  openUnifiedTimetable = () => {
    const pid = this.resolvedPlaygroundId();
    if (!pid || !this.props.history) return;
    const date = new Date().toISOString().slice(0, 10);
    const sectionId = this.props.initialSectionFilterId;
    const sectionQ = sectionId
      ? `&sectionId=${encodeURIComponent(String(sectionId))}`
      : '';
    this.props.history.push(
      `/playground/${pid}?date=${encodeURIComponent(date)}${sectionQ}`,
    );
  };

  openWorkingHours = () => {
    const pid = this.resolvedPlaygroundId();
    if (!pid || !this.props.history) return;
    this.props.history.push(`/playground/${pid}`);
  };

  handlePlaygroundChange = (event) => {
    if (!this.props.onPlaygroundChange) return;
    this.props.onPlaygroundChange(String(event.target.value));
  };

  render() {
    const {
      classes,
      playgrounds,
      sportComplexId,
      initialSectionFilterId,
    } = this.props;
    const list = playgrounds || [];
    const selectedId = this.resolvedPlaygroundId();

    return (
      <Paper variant="outlined" className={classes.unifiedScheduleBox} data-testid="unified-hall-schedule">
        <Typography variant="subtitle1" className={classes.title}>
          Единое расписание площадки (зала)
        </Typography>
        <Typography variant="body2" color="textSecondary" className={classes.fieldHint}>
          Аренда, блокировки, занятия секций и персональные на одной шкале. Слоты секций заняты для аренды.
        </Typography>
        <Typography variant="body2" className={classes.fieldHintLast}>
          Если календарь пустой — заполните <strong>режим работы</strong> площадки. Для секций без аренды
          цена слота может быть <strong>0</strong>.
        </Typography>
        <div className={classes.unifiedScheduleRow}>
          <FormControl className={classes.unifiedScheduleSelect}>
            <InputLabel>Площадка</InputLabel>
            <Select
              value={selectedId}
              onChange={this.handlePlaygroundChange}
              disabled={!list.length}
            >
              {list.map((p) => {
                const id = playgroundIdOf(p);
                if (!id) return null;
                return (
                  <MenuItem key={id} value={id}>
                    {playgroundNameOf(p)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <div className={classes.unifiedScheduleActions}>
            <Button
              className={classes.unifiedScheduleAction}
              size="small"
              variant="outlined"
              color="primary"
              disabled={!list.length}
              onClick={this.openUnifiedTimetable}
            >
              Открыть на всю страницу
            </Button>
            <Button
              className={classes.unifiedScheduleAction}
              size="small"
              variant="contained"
              color="primary"
              disabled={!list.length}
              onClick={this.openWorkingHours}
            >
              Настроить режим работы
            </Button>
          </div>
        </div>
        {!list.length && (
          <Typography variant="body2" color="error" className={classes.emptyError}>
            У комплекса нет площадок — добавьте площадку, чтобы видеть единый таймлайн.
          </Typography>
        )}
        {!!list.length && selectedId && (
          <PlaygroundTimetableEmbed
            playgroundId={Number(selectedId)}
            sportComplexId={sportComplexId}
            initialSectionFilterId={initialSectionFilterId || ''}
          />
        )}
      </Paper>
    );
  }
}

const styles = (theme) => ({
  unifiedScheduleBox: {
    padding: theme.spacing(2.5),
    marginBottom: 0,
    backgroundColor: 'rgba(123, 31, 162, 0.04)',
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.75),
  },
  fieldHint: {
    marginBottom: theme.spacing(0.5),
  },
  fieldHintLast: {
    marginBottom: 0,
  },
  unifiedScheduleRow: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'flex-end',
    marginTop: theme.spacing(2),
    gap: 0,
  },
  unifiedScheduleSelect: {
    flex: '1 1 auto',
    minWidth: 0,
    maxWidth: 'none',
  },
  unifiedScheduleActions: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: '0 0 auto',
    maxWidth: '100%',
    minWidth: 0,
    marginLeft: theme.spacing(1.5),
  },
  unifiedScheduleAction: {
    flex: '0 0 auto',
    minHeight: 30,
    padding: '4px 10px',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: 6,
    whiteSpace: 'nowrap',
    lineHeight: 1.25,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
    '& + &': {
      marginLeft: 8,
    },
  },
  emptyError: {
    marginTop: theme.spacing(1.5),
  },
});

export default withStyles(styles)(UnifiedHallSchedule);
